import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Font_Fallbacks = () => {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🔄 CSS Font Fallbacks</h1>

      {/* Intro */}
      <div
        style={{
          background: "linear-gradient(135deg, #d1fae5, #bbf7d0)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#166534", marginBottom: "0.5rem" }}>
          ✨ Why Font Fallbacks?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          Not all users have the same fonts installed. A{" "}
          <strong>font fallback</strong> ensures your text still looks good if
          the first choice isn’t available. Browsers read fonts from left to
          right and apply the first one that works.
        </p>
      </div>

      {/* Basic Example */}
      <div className="tutorial-section">
        <h2 className="section-title">📘 Basic Example</h2>
        <p>
          If <code>Arial</code> isn’t installed, the browser will try{" "}
          <code>Helvetica</code>, and finally fall back to{" "}
          <code>sans-serif</code>.
        </p>

        <CodeCopyBlock
          code={`body {
  font-family: "Arial", "Helvetica", sans-serif;
}`}
        />

        <p style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
          👀 Example Text using font fallbacks
        </p>
      </div>

      {/* With Custom Fonts */}
      <div className="tutorial-section">
        <h2 className="section-title">🌍 Fallbacks with Custom Fonts</h2>
        <p>
          When using Google Fonts or custom fonts, always include{" "}
          <strong>system defaults</strong> as a backup:
        </p>

        <CodeCopyBlock
          code={`h1 {
  font-family: "Roboto", "Helvetica Neue", Arial, sans-serif;
}`}
        />

        <h3 style={{ fontFamily: "Roboto, Helvetica Neue, Arial, sans-serif" }}>
          Example: This heading tries Roboto first
        </h3>
      </div>

      {/* Generic Families */}
      <div className="tutorial-section">
        <h2 className="section-title">📂 Generic Font Families</h2>
        <p>
          Always end your font stack with a <strong>generic family</strong> so
          the browser can pick a safe option:
        </p>
        <ul className="tutorial-list">
          <li>
            <code>serif</code> → Times, Georgia
          </li>
          <li>
            <code>sans-serif</code> → Arial, Helvetica, Verdana
          </li>
          <li>
            <code>monospace</code> → Courier New, Lucida Console
          </li>
          <li>
            <code>cursive</code> → Comic Sans, Brush Script MT
          </li>
          <li>
            <code>fantasy</code> → Papyrus, Impact
          </li>
        </ul>
      </div>

      {/* Example Comparison */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example Comparison</h2>
        <p>Notice how the same text looks with different fallback stacks:</p>

        <div style={{ marginBottom: "1rem" }}>
          <p style={{ fontFamily: "Georgia, serif" }}>
            Georgia → Serif fallback
          </p>
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <p style={{ fontFamily: "Verdana, sans-serif" }}>
            Verdana → Sans-serif fallback
          </p>
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <p style={{ fontFamily: "Courier New, monospace" }}>
            Courier New → Monospace fallback
          </p>
        </div>
      </div>

      {/* Best Practices */}
      <div className="tutorial-section">
        <h2 className="section-title">💡 Best Practices</h2>
        <ul className="tutorial-list">
          <li>
            Always include <strong>at least 2-3 fonts</strong> in your stack.
          </li>
          <li>
            End with a <strong>generic family</strong> (like sans-serif).
          </li>
          <li>
            Match fonts by <strong>style and tone</strong> (don’t mix serif with
            monospace unnecessarily).
          </li>
        </ul>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          After learning about <strong>font fallbacks</strong>, the next step is
          to explore{" "}
          <a href="/css/google-fonts" className="learn-more">
            Google Fonts
          </a>{" "}
          to use modern and stylish fonts in your projects.
        </p>
      </div>
    </div>
  );
};

export default Font_Fallbacks;
